import { useCallback, useMemo, useState } from "react";

import noop from "lodash/noop";

import type { IBreadcrumbs } from "@similarweb/ui-components";
import { TabList, TabPanel, Tabs, Breadcrumbs, PixelPlaceholderLoader } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import SettingsActions from "app/pages/activation/components/Settings/SettingsActions/SettingsActions";
import { GeneralTabContent } from "app/pages/activation/components/Settings/TabsContent/GeneralTab/GeneralTab";
import { NewsSettingsTab } from "app/pages/activation/components/Settings/TabsContent/NewsSettingsTab/NewsSettingsTab";
import { SubscriptionTabContent } from "app/pages/activation/components/Settings/TabsContent/SubscriptionTab";
import { UnsavedModal } from "app/pages/activation/components/Settings/UnsavedModal/UnsavedModal";
import { StyledTab } from "app/pages/activation/components/Shared/StyledTab/StyledTab";
import { SubscriptionProvider } from "app/pages/activation/context/Subscription/SubscriptionProvider";
import { onCrumbClick } from "app/pages/activation/helpers/onBreadcrumbClick";
import { useTabs } from "app/pages/activation/hooks/helpers/useTabs";
import { useSaveSettings } from "app/pages/activation/hooks/settings/useSaveSettings";
import { getCrumbList, TAB_DICTIONARY, TABS_ARRAY } from "app/pages/activation/modules/Settings/config";
import useActivationObjectQuery from "app/pages/activation/queries/data/useActivationObjectQuery";
import { ACTIVATION_PROJECT_ROUTE, DEFAULT_ACTIVATION_GRANULARITY } from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ActivationComponentAssets } from "../../helpers/ActivationAssets";
import { generateAutomationName } from "../../helpers/generateAutomationName";

import * as SC from "./styles";
import { SettingsTabs } from "./types";

const Settings = () => {
    const apiParams = proNavigator.getApiParams();

    const activationId = apiParams?.key as string;
    const [isChanged, setIsChanged] = useState(false);

    const { data, isLoading } = useActivationObjectQuery(activationId);
    const activationName = data?.name ?? "";

    const canEdit = !!data?.canEdit;
    const visibleTabs = useMemo(
        () => (canEdit ? TABS_ARRAY : TABS_ARRAY.filter((tab) => tab !== SettingsTabs.NewsSettings)),
        [canEdit],
    );

    const { tabIndex, onTabSelect } = useTabs({ tabs: visibleTabs, isLoading });

    const { onSave, setDataToSave, isSaveLoading, isSaveDisabled, competitors } = useSaveSettings({
        activationId,
        currentTab: visibleTabs[tabIndex],
        isChanged,
    });

    const [unsavedModalProps, setUnsavedModalProps] = useState({ isOpen: false, targetFunction: noop });

    const onBackHome = () =>
        proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
            ...apiParams,
            country: data?.country,
            timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
        });

    const onCancel = () => {
        activationTracking.settings.trackCancel();

        if (isChanged) {
            setUnsavedModalProps({ isOpen: true, targetFunction: onBackHome });

            return;
        }

        onBackHome();
    };

    const onChangeTab = (index: number) => {
        const name = visibleTabs[index];
        activationTracking.settings.trackTabChange(name);

        if (isChanged) {
            setIsChanged(false);

            setUnsavedModalProps({
                isOpen: true,
                targetFunction: () => {
                    onTabSelect(index);
                    setUnsavedModalProps({ isOpen: false, targetFunction: noop });
                },
            });

            return;
        }

        onTabSelect(index);
    };

    const onBreadCrumbClick: IBreadcrumbs["onClick"] = (item) => {
        if (isChanged) {
            setUnsavedModalProps({
                isOpen: true,
                targetFunction: () => {
                    void onCrumbClick(item);
                },
            });

            return;
        }

        void onCrumbClick(item);
    };

    const onModalClose = useCallback(() => setUnsavedModalProps({ isOpen: false, targetFunction: noop }), []);

    const saveWithoutRedirect = useCallback(() => onSave(false), [onSave]);
    const maxCompetitors = ActivationComponentAssets.competitorsLimit;

    return (
        <SC.SettingsContainer data-automation={generateAutomationName("settings", ["container"])}>
            <SC.SettingsWithButtonsWrapper>
                <Breadcrumbs
                    list={getCrumbList(activationName)}
                    onClick={onBreadCrumbClick}
                    isLoading={isLoading}
                />
                <SC.TitleWrapper>{activationName}</SC.TitleWrapper>

                <SC.SettingsShadowWrapper>
                    {isLoading ? (
                        <PixelPlaceholderLoader width="100%" />
                    ) : (
                        <Tabs
                            selectedIndex={tabIndex}
                            onSelect={onChangeTab}
                        >
                            <SC.SettingsWrapper id="settingsWrapper">
                                <SC.TabsContainer>
                                    <TabList>
                                        {visibleTabs.map((id) => (
                                            <StyledTab
                                                key={id}
                                                data-automation={generateAutomationName("settings", ["tab", id])}
                                            >
                                                {translate(TAB_DICTIONARY[id].title)}
                                            </StyledTab>
                                        ))}
                                    </TabList>
                                </SC.TabsContainer>

                                <SC.SettingsContent
                                    data-automation={generateAutomationName("settings", ["content", "wrapper"])}
                                >
                                    <TabPanel>
                                        <GeneralTabContent
                                            setIsChanged={setIsChanged}
                                            isChanged={isChanged}
                                            setDataToSave={setDataToSave}
                                            isViewerMode={!canEdit}
                                        />
                                    </TabPanel>

                                    <TabPanel>
                                        <SubscriptionProvider
                                            isViewerMode={!canEdit}
                                            activationId={activationId}
                                            benchmarkId={data?.assets?.benchmarkId}
                                        >
                                            <SubscriptionTabContent
                                                setIsChanged={setIsChanged}
                                                setDataToSave={setDataToSave}
                                            />
                                        </SubscriptionProvider>
                                    </TabPanel>

                                    {canEdit ? (
                                        <TabPanel>
                                            <NewsSettingsTab
                                                activationId={activationId}
                                                activationDomains={data?.allDomains ?? []}
                                                primaryDomain={data?.primaryDomain}
                                                setIsChanged={setIsChanged}
                                                setDataToSave={setDataToSave}
                                            />
                                        </TabPanel>
                                    ) : null}
                                </SC.SettingsContent>

                                <SettingsActions
                                    isDisabled={isSaveDisabled}
                                    onCancel={onCancel}
                                    onSave={onSave}
                                    isLoading={isSaveLoading}
                                    zIndex={competitors.length ? 3 : 1}
                                />
                            </SC.SettingsWrapper>
                        </Tabs>
                    )}
                </SC.SettingsShadowWrapper>
            </SC.SettingsWithButtonsWrapper>

            <UnsavedModal
                isOverTheLimit={competitors.length > maxCompetitors}
                isOpen={unsavedModalProps.isOpen}
                onClose={onModalClose}
                onConfirm={saveWithoutRedirect}
                targetFunction={unsavedModalProps.targetFunction}
            />
        </SC.SettingsContainer>
    );
};

export default Settings;
